import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowRight, Download, Loader2, Sparkles } from "lucide-react";
import api from "../lib/api";
import ArchetypeShareCard from "../components/ArchetypeShareCard";

/**
 * Public landing page for an individual collector's archetype.
 * Designed to be the click-target of social shares from `ArchetypeShareCard` PNGs.
 * Route: /c/:slug/archetype
 *
 * Full-bleed (no sidebar). Loads via the public archetype endpoint —
 * works for any public profile, 403 on private, 404 on unknown.
 */
export default function ArchetypeLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const captureRef = useRef(null);

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/collectors/${encodeURIComponent(slug)}/archetype`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 404) setError("This collector doesn't exist.");
        else if (status === 403) setError("This collector's profile is private.");
        else setError("Couldn't load this archetype right now.");
      });
  }, [slug]);

  const handleSave = async () => {
    if (!data || exporting) return;
    setExporting(true);
    setShowCapture(true);
    await new Promise((r) => setTimeout(r, 200));
    try {
      const node = captureRef.current;
      if (!node) throw new Error("capture node missing");
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#06060a",
        width: 1080,
        height: 1080,
      });
      const archName = (data.archetype?.name || "archetype").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      const link = document.createElement("a");
      link.download = `castfolio-${archName}-${slug}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Saved to your device");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save image");
    } finally {
      setExporting(false);
      setShowCapture(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center text-center p-8" data-testid="archetype-landing-error">
        <p className="text-rose-400 mb-4">{error}</p>
        <Button onClick={() => navigate("/")} data-testid="archetype-landing-error-home">Go home</Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="archetype-landing-loading">
        <Loader2 className="w-10 h-10 text-zinc-400 animate-spin" />
      </div>
    );
  }

  const { archetype, stats, user } = data;
  const owner = {
    name: user?.name,
    profile_slug: user?.profile_slug || slug,
    profile_photo: user?.profile_photo,
  };

  return (
    <div
      className="min-h-screen text-white relative overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 80% -10%, rgba(244,114,182,0.18) 0%, transparent 55%), radial-gradient(circle at -10% 110%, rgba(251,191,36,0.10) 0%, transparent 55%), linear-gradient(140deg,#06060a 0%,#0f0817 60%,#1a0a26 100%)",
      }}
      data-testid="archetype-landing-page"
    >
      {/* Top bar */}
      <header className="relative z-10 px-6 sm:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="archetype-landing-brand">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-xl"
            style={{ background: "linear-gradient(135deg,#f43f5e,#a855f7)" }}
          >
            C
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold tracking-tight text-lg">Castfolio</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Your diecast portfolio</div>
          </div>
        </Link>
        <Link
          to={`/collector/${owner.profile_slug}`}
          className="text-xs sm:text-sm text-zinc-400 hover:text-white underline-offset-4 hover:underline"
          data-testid="archetype-landing-view-profile"
        >
          View full profile →
        </Link>
      </header>

      {/* Body */}
      <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pt-6 pb-20 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* Left: archetype text + CTA */}
        <div>
          <p className="text-amber-300 text-xs uppercase tracking-[0.4em] font-semibold flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Collector Archetype
          </p>
          <h1
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black mt-4 leading-[0.98] tracking-tight"
            style={{
              background: "linear-gradient(135deg,#fff 0%,#fbcfe8 60%,#f0abfc 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
            data-testid="archetype-landing-name"
          >
            {archetype?.name}
          </h1>
          <p className="mt-5 text-lg text-zinc-300 max-w-xl leading-relaxed">{archetype?.tagline}</p>
          <p className="mt-8 text-sm text-zinc-400">
            {owner.name ? (
              <>
                <span className="text-zinc-200 font-semibold">{owner.name}</span>
                {owner.profile_slug && <span className="text-zinc-500"> · @{owner.profile_slug}</span>}
              </>
            ) : (
              <>@{owner.profile_slug}</>
            )}
          </p>
          {/* Quick stats inline */}
          <div className="mt-10 grid grid-cols-4 gap-4 max-w-md">
            <MiniStat label="Pieces" value={(stats?.pieces ?? 0).toLocaleString()} />
            <MiniStat label="Brands" value={stats?.brand_diversity ?? 0} />
            <MiniStat label="Chases" value={stats?.chase_count ?? 0} accent="text-amber-300" />
            <MiniStat label="Grails" value={stats?.grail_count ?? 0} accent="text-rose-300" />
          </div>
          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => navigate("/register")}
              className="bg-gradient-to-r from-rose-500 to-fuchsia-500 hover:from-rose-400 hover:to-fuchsia-400 text-white font-semibold h-12 px-6"
              data-testid="archetype-landing-cta-signup"
            >
              Find your archetype <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={exporting}
              className="border-zinc-700 hover:border-zinc-500 bg-transparent hover:bg-white/5 h-12 px-6 text-zinc-200"
              data-testid="archetype-landing-save-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Save card
            </Button>
          </div>
          <p className="mt-5 text-xs text-zinc-500">
            Castfolio assigns a collector personality based on what's in your shelf — chases, grails, brands, the works.
          </p>
        </div>

        {/* Right: visual share card preview (responsive scale) */}
        <div className="flex items-center justify-center">
          <div
            className="rounded-3xl ring-1 ring-white/10 shadow-2xl shadow-fuchsia-900/30 overflow-hidden relative"
            style={{ width: "min(100%, 540px)", aspectRatio: "1 / 1" }}
          >
            {/* Scaled-down 1080² card. Outer wraps to a max of 540px (50% scale). */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 1080,
                height: 1080,
                transformOrigin: "top left",
                transform: "scale(0.5)",
              }}
            >
              <ArchetypeShareCard archetype={archetype} stats={stats} owner={owner} />
            </div>
          </div>
        </div>
      </main>

      {/* Off-screen capture (full-size) */}
      {showCapture && (
        <div aria-hidden="true" style={{ position: "fixed", left: -99999, top: 0, zIndex: -1, pointerEvents: "none" }}>
          <ArchetypeShareCard archetype={archetype} stats={stats} owner={owner} innerRef={captureRef} />
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent = "text-white" }) {
  return (
    <div>
      <div className={`text-2xl font-black ${accent}`}>{value}</div>
      <div className="text-zinc-500 text-[10px] uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}
